/**
 * Remappable keyboard shortcuts store.
 * Only single-key (no modifier) action shortcuts are remappable.
 * Complex multi-key combos (Ctrl+Z, Ctrl+S, etc.) remain hardcoded.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type RemappableAction =
  | 'play'
  | 'toolSelect'
  | 'toolMove'
  | 'toolRotate'
  | 'toolScale'
  | 'toolPan'
  | 'toggleBones'
  | 'toggleTimeline'
  | 'toggleOnion'
  | 'fitView'
  | 'togglePresentation'
  | 'zoomIn'
  | 'zoomOut';

export interface ShortcutBinding {
  key: string;         // e.g. 'v', 'g', ' '
  display: string;     // e.g. 'V', 'G', 'Space'
  label: string;       // human-readable action name
  group: string;
}

export const DEFAULT_BINDINGS: Record<RemappableAction, ShortcutBinding> = {
  play:               { key: ' ',  display: 'Space', label: 'Play / Pause',       group: 'Playback' },
  toolPan:            { key: 'q',  display: 'Q',     label: 'Pan tool',            group: 'Tools' },
  toolSelect:         { key: 'w',  display: 'W',     label: 'Select tool',         group: 'Tools' },
  toolMove:           { key: 'e',  display: 'E',     label: 'Move / Gizmo tool',   group: 'Tools' },
  toolRotate:         { key: 'r',  display: 'R',     label: 'Rotate tool',         group: 'Tools' },
  toolScale:          { key: 't',  display: 'T',     label: 'Scale tool',          group: 'Tools' },
  toggleBones:        { key: 'b',  display: 'B',     label: 'Toggle bones',        group: 'Tools' },
  toggleTimeline:     { key: 'y',  display: 'Y',     label: 'Toggle timeline',     group: 'View' },
  toggleOnion:        { key: 'o',  display: 'O',     label: 'Toggle onion skin',   group: 'View' },
  fitView:            { key: 'f',  display: 'F',     label: 'Fit view to artboard', group: 'View' },
  togglePresentation: { key: 'F5', display: 'F5',   label: 'Presentation mode',   group: 'View' },
  zoomIn:             { key: '+',  display: '+',     label: 'Zoom in',             group: 'View' },
  zoomOut:            { key: '-',  display: '−',     label: 'Zoom out',            group: 'View' },
};

interface ShortcutsState {
  bindings: Record<RemappableAction, ShortcutBinding>;
  setBinding: (action: RemappableAction, key: string, display: string) => void;
  resetBinding: (action: RemappableAction) => void;
  resetAll: () => void;
  /** Returns the action mapped to a key, or null */
  getActionForKey: (key: string) => RemappableAction | null;
}

export const useShortcutsStore = create<ShortcutsState>()(
  persist(
    (set, get) => ({
      bindings: { ...DEFAULT_BINDINGS },

      setBinding: (action, key, display) => {
        // Remove any existing binding for this key first (avoid duplicates)
        const current = get().bindings;
        const updated: Record<RemappableAction, ShortcutBinding> = { ...current };
        for (const [a, b] of Object.entries(updated) as [RemappableAction, ShortcutBinding][]) {
          if (b.key === key && a !== action) {
            updated[a] = { ...b, key: '', display: '—' };
          }
        }
        updated[action] = { ...updated[action], key, display };
        set({ bindings: updated });
      },

      resetBinding: (action) =>
        set((s) => ({
          bindings: { ...s.bindings, [action]: { ...DEFAULT_BINDINGS[action] } },
        })),

      resetAll: () => set({ bindings: { ...DEFAULT_BINDINGS } }),

      getActionForKey: (key) => {
        const bindings = get().bindings;
        for (const [action, binding] of Object.entries(bindings) as [RemappableAction, ShortcutBinding][]) {
          if (binding.key === key) return action;
        }
        return null;
      },
    }),
    { name: 'spine-shortcuts' }
  )
);
